import type { ConversationMessage, ConversationQuestion } from '@/api/conversation/conversation'
import { selectAnchoredTranscriptTimeline, selectSlideVersionFacts } from './selectors'
import type { ConversationState } from './reducer'
import type { TranscriptEntry } from './selectors'

export type ConversationTurnPhase = 'submitted' | 'working' | 'waiting' | 'writing' | 'complete' | 'stopped'
export type ConversationOriginStatus = 'left-unfinished'

type ConversationTask = ConversationState['tasks'][number]
type ConversationAttemptEntry = Extract<TranscriptEntry, { kind: 'attempt' }>

export type ConversationClarification = {
  message: ConversationMessage
  slots: Array<{ key: string; label: string }>
}

export type ConversationTurnPresentation = {
  key: string
  phase: ConversationTurnPhase
  isLatestPendingQuestion: boolean
  entries: Array<TranscriptEntry>
  tasks: Array<ConversationTask>
  resultMessages: Array<ConversationMessage>
  clarifications: Array<ConversationClarification>
  questions: Array<ConversationQuestion>
  attempts: Array<ConversationAttemptEntry>
  activity: Array<TranscriptEntry>
  executionSteps: Array<string>
  meaningfulActivityCount: number
  activeStage?: string
  activeSummary?: string
  startedAtTsMs?: number
  originMessage?: ConversationMessage
  originStatus?: ConversationOriginStatus
}

type MutableTurn = Omit<
  ConversationTurnPresentation,
  'isLatestPendingQuestion' | 'phase' | 'meaningfulActivityCount'
> & {
  order: number
}

function uniqueTasks(tasks: ReadonlyArray<ConversationTask>): Array<ConversationTask> {
  const byRun = new Map<number, ConversationTask>()
  for (const task of tasks) byRun.set(task.intakeRunId, task)
  return [...byRun.values()]
}

function executionSteps(tasks: ReadonlyArray<ConversationTask>, state: ConversationState): Array<string> {
  const steps: Array<string> = []
  const seen = new Set<string>()
  for (const task of tasks) {
    if (task.executionId === null || task.executionId === undefined) continue
    for (const step of state.executionStates[task.executionId]?.steps ?? []) {
      // This is useful while text is arriving, but repeats the existence of the final
      // response once the turn settles. Keep completed disclosures for work that adds
      // information beyond the answer itself.
      if (step === 'generate-output') continue
      if (seen.has(step)) continue
      seen.add(step)
      steps.push(step)
    }
  }
  return steps
}

export function isTranscriptActivity(entry: TranscriptEntry): boolean {
  return !(entry.kind === 'change' && entry.change.kind === 'question' && entry.change.transition === 'abandoned')
}

function turnPhase(turn: MutableTurn, state: ConversationState): ConversationTurnPhase {
  if (turn.questions.length > 0) return 'waiting'

  for (const task of turn.tasks) {
    if (task.executionId !== null && task.executionId !== undefined) {
      const live = state.executionStates[task.executionId]
      if (live?.status === 'queued' || live?.status === 'running') return 'working'
    }
    if (task.executionStatus === 'queued' || task.executionStatus === 'running') return 'working'
  }

  if (
    turn.activity.some(
      (entry) => entry.kind === 'stopped' || entry.kind === 'intake-stopped' || entry.kind === 'exhausted'
    )
  ) {
    return 'stopped'
  }

  if (turn.resultMessages.length > 0) return 'complete'
  if (
    turn.tasks.some((task) => task.executionStatus === 'succeeded') ||
    turn.tasks.some(
      (task) =>
        task.executionId !== null &&
        task.executionId !== undefined &&
        state.executionStates[task.executionId]?.status === 'succeeded'
    )
  ) {
    return 'complete'
  }
  if (turn.attempts.length > 0) return 'writing'
  if (turn.activity.length > 0) return 'complete'
  if (turn.tasks.some((task) => task.intakeStatus === 'queued' || task.intakeStatus === 'running')) return 'submitted'
  if (
    turn.originMessage?.author === 'user' &&
    state.pendingCanonicalMessageIds.includes(turn.originMessage.messageId)
  ) {
    return 'submitted'
  }
  return 'complete'
}

function activeExecutionState(
  tasks: ReadonlyArray<ConversationTask>,
  attempts: ReadonlyArray<ConversationAttemptEntry>,
  state: ConversationState
): { activeStage?: string; activeSummary?: string; startedAtTsMs?: number } {
  for (const task of [...tasks].reverse()) {
    if (task.executionId !== null && task.executionId !== undefined) {
      const execution = state.executionStates[task.executionId]
      if (
        execution?.status === 'queued' ||
        execution?.status === 'running' ||
        task.executionStatus === 'queued' ||
        task.executionStatus === 'running'
      ) {
        const activeStage = execution?.stage ?? task.executionStage
        const activeSummary = execution?.summary
        const startedAtTsMs = state.executionStartedAt[task.executionId]
        return {
          ...(activeStage === undefined || activeStage === '' ? {} : { activeStage }),
          ...(activeSummary === undefined || activeSummary === '' ? {} : { activeSummary }),
          ...(startedAtTsMs === undefined ? {} : { startedAtTsMs })
        }
      }
    }
    if (task.intakeStatus === 'queued' || task.intakeStatus === 'running') {
      const activeStage = task.questionStage || task.intakeStage
      return activeStage === '' ? {} : { activeStage }
    }
  }
  // Stream text can precede the refreshed task snapshot that links its execution back to
  // the user turn. The attempt still names the execution, so keep its specific stage
  // instead of dropping the live announcement to generic writing copy during that gap.
  for (const entry of [...attempts].reverse()) {
    const execution = state.executionStates[entry.attempt.executionId]
    const activeStage = execution?.stage || entry.attempt.stepKey
    const activeSummary = execution?.summary
    const startedAtTsMs = state.executionStartedAt[entry.attempt.executionId]
    return {
      ...(activeStage === '' ? {} : { activeStage }),
      ...(activeSummary === undefined || activeSummary === '' ? {} : { activeSummary }),
      ...(startedAtTsMs === undefined ? {} : { startedAtTsMs })
    }
  }
  return {}
}

export function selectConversationTurnPresentations(state: ConversationState): Array<ConversationTurnPresentation> {
  const messages = [...state.olderMessages, ...state.liveMessages]
  const messagesById = new Map(messages.map((entry) => [entry.messageId, entry]))
  const tasks = uniqueTasks([...state.olderTasks, ...state.tasks])
  const tasksByOrigin = new Map<number, Array<ConversationTask>>()
  const outputOwner = new Map<number, number>()
  for (const task of tasks) {
    const owned = tasksByOrigin.get(task.originMessageId) ?? []
    owned.push(task)
    tasksByOrigin.set(task.originMessageId, owned)
    for (const messageId of task.executionOutputMessageIds) outputOwner.set(messageId, task.originMessageId)
  }

  const briefsById = new Map(state.briefs.map((brief) => [brief.briefId, brief]))
  const taskOriginMessageIds = new Set(tasks.map((task) => task.originMessageId))
  const clarificationOwner = new Map<
    number,
    { originMessageId: number; slots: Array<{ key: string; label: string }> }
  >()
  for (const task of tasks) {
    if (task.briefId === null || task.briefId === undefined) continue
    const brief = briefsById.get(task.briefId)
    if (brief === undefined) continue
    for (const slot of brief.slots) {
      const messageId = slot.source?.messageId
      if (
        slot.provenance !== 'user-stated' ||
        messageId === undefined ||
        messageId === task.originMessageId ||
        taskOriginMessageIds.has(messageId)
      )
        continue
      if (messagesById.get(messageId)?.author !== 'user') continue
      const owned = clarificationOwner.get(messageId)
      if (owned !== undefined && owned.originMessageId !== task.originMessageId) continue
      const slots = owned?.slots ?? []
      if (!slots.some((candidate) => candidate.key === slot.key)) slots.push({ key: slot.key, label: slot.label })
      clarificationOwner.set(messageId, { originMessageId: task.originMessageId, slots })
    }
  }

  const pairedSlideEvents = selectSlideVersionFacts(state).pairedEventIds
  const turns = new Map<string, MutableTurn>()
  let fallbackOrder = 0

  const ensureTurn = (key: string, originMessageId?: number): MutableTurn => {
    const existing = turns.get(key)
    if (existing !== undefined) return existing
    const originMessage = originMessageId === undefined ? undefined : messagesById.get(originMessageId)
    const created: MutableTurn = {
      key,
      order: originMessage?.createdAtTsMs ?? Number.MAX_SAFE_INTEGER + fallbackOrder++,
      entries: [],
      tasks: originMessageId === undefined ? [] : (tasksByOrigin.get(originMessageId) ?? []),
      resultMessages: [],
      clarifications: [],
      questions: [],
      attempts: [],
      activity: [],
      executionSteps: [],
      ...(originMessage === undefined ? {} : { originMessage })
    }
    turns.set(key, created)
    return created
  }

  for (const placement of selectAnchoredTranscriptTimeline(state)) {
    const { entry } = placement
    const outputOrigin = entry.kind === 'message' ? outputOwner.get(entry.message.messageId) : undefined
    const clarification = entry.kind === 'message' ? clarificationOwner.get(entry.message.messageId) : undefined
    const originMessageId = outputOrigin ?? clarification?.originMessageId ?? placement.originMessageId
    const key = originMessageId === undefined ? `unanchored:${entry.key}` : `turn:${originMessageId}`
    const turn = ensureTurn(key, originMessageId)
    turn.entries.push(entry)

    switch (entry.kind) {
      case 'message':
        if (clarification !== undefined) {
          turn.clarifications.push({ message: entry.message, slots: clarification.slots })
        } else if (outputOrigin !== undefined || entry.message.author !== 'user') {
          turn.resultMessages.push(entry.message)
        }
        break
      case 'question':
        turn.questions.push(entry.question)
        break
      case 'attempt':
        turn.attempts.push(entry)
        break
      case 'change':
        if (entry.change.kind === 'question' && entry.change.transition === 'abandoned') {
          turn.originStatus = 'left-unfinished'
        }
        if (!pairedSlideEvents.has(entry.change.eventId)) turn.activity.push(entry)
        break
      case 'stopped':
      case 'intake-stopped':
      case 'exhausted':
        turn.activity.push(entry)
        break
    }
  }

  const ordered = [...turns.values()].sort(
    (left, right) => left.order - right.order || left.key.localeCompare(right.key)
  )
  const latestQuestionKey = [...ordered].reverse().find((turn) => turn.questions.length > 0)?.key

  return ordered.map((turn) => {
    const steps = executionSteps(turn.tasks, state)
    const active = activeExecutionState(turn.tasks, turn.attempts, state)
    return {
      key: turn.key,
      phase: turnPhase(turn, state),
      isLatestPendingQuestion: turn.key === latestQuestionKey,
      entries: turn.entries,
      tasks: turn.tasks,
      resultMessages: turn.resultMessages,
      clarifications: turn.clarifications,
      questions: turn.questions,
      attempts: turn.attempts,
      activity: turn.activity,
      executionSteps: steps,
      meaningfulActivityCount: steps.length + turn.activity.filter(isTranscriptActivity).length,
      ...active,
      ...(turn.originMessage === undefined ? {} : { originMessage: turn.originMessage }),
      ...(turn.originStatus === undefined ? {} : { originStatus: turn.originStatus })
    }
  })
}

export function selectActiveConversationTurn(
  turns: ReadonlyArray<ConversationTurnPresentation>
): ConversationTurnPresentation | undefined {
  return [...turns].reverse().find((turn) => turn.phase !== 'complete')
}
